import { useAuth0 } from "@auth0/auth0-react";
import { useState } from "react";

import {
  announcementsForEmail,
  dismissalKey,
  type AnnouncementKind,
} from "../../lib/announcements";

// Mirrors the BannerStack palette so announcements read like the rest of the
// shell's banners.
const KIND_CLASSES: Record<AnnouncementKind, string> = {
  info: "border-outline bg-info/10 text-on-surface",
  warning: "border-outline bg-warning/10 text-on-surface",
};

function isDismissed(id: string): boolean {
  try {
    return window.localStorage.getItem(dismissalKey(id)) === "1";
  } catch {
    // localStorage unavailable (private mode): treat as not dismissed.
    return false;
  }
}

// One-time announcements targeted at the signed-in account. Dismissal is read
// from / written to localStorage at render time, so an announcement whose
// audience only matches once Auth0 has loaded the user's email still respects a
// prior dismissal.
export function AnnouncementBanner() {
  const { user } = useAuth0();
  const email = typeof user?.email === "string" ? user.email : undefined;
  const [, forceRerender] = useState(0);

  const visible = announcementsForEmail(email).filter((a) => !isDismissed(a.id));
  if (visible.length === 0) {
    return null;
  }

  function dismiss(id: string): void {
    try {
      window.localStorage.setItem(dismissalKey(id), "1");
    } catch {
      // Persistence failed; still hide for this session via the re-render.
    }
    forceRerender((n) => n + 1);
  }

  return (
    <div data-testid="announcement-stack">
      {visible.map((a) => (
        <div
          key={a.id}
          role="status"
          aria-live="polite"
          data-testid={`announcement-${a.id}`}
          className={`flex items-start gap-3 border-b px-4 py-2 text-sm ${KIND_CLASSES[a.kind]}`}
        >
          <span className="flex-1">
            <strong className="font-semibold">{a.title}</strong>{" "}
            <span className="text-on-surface-variant">{a.body}</span>
          </span>
          <button
            type="button"
            onClick={() => dismiss(a.id)}
            aria-label="Dismiss announcement"
            data-testid={`announcement-${a.id}-dismiss`}
            className="shrink-0 rounded-control px-2 py-1 text-on-surface-variant hover:bg-surface-container"
          >
            &times;
          </button>
        </div>
      ))}
    </div>
  );
}
